import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { URLS, APIS, SF_CO_SRCH, SG_CO_LIST, SG_CO_MGMT_EXCEL_LIST  } from "@/tra/tancis/ptli/usr/co/services/PtliCoService";
import dayjs from "dayjs";

/*
 * Company Management List
 * !== 업체 관리 목록 ==!
 */
export const PTLI0905001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [load, setLoad] = useState(false);

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Company Approval Request List Search !== 업체 목록 검색 ==!
        coListSrch: useForm({
            defaultSchema: SF_CO_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Company Approval Request List !== 업체 목록 ==!
        coList: useGrid({
            defaultSchema: SG_CO_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        coExcelList: useGrid({
            defaultSchema: SG_CO_MGMT_EXCEL_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Company Approval Request List !==  업체 목록 조회==!
        getCoList: useFetch({
            api: (page = grid.coList.page) => APIS.getCoList(form.coListSrch.getValues(), page, grid.coList.size),
            enabled: comnUtils.isEmpty(form.coListSrch.errors),
            key: [grid.coList.page, grid.coList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.coListSrch.getValues(),
                    page: grid.coList.page,
                    size: grid.coList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
        getCoExcelList: useFetch({  
            api: (data: any) => (load ? APIS.getCoExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.coListSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.coExcelList.exportExcel({
                        excelName: `${t("L_INT_PTL")}_${t("L_CO")}_${t("L_MGMT_LST")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),
    };
    const render = {
        cell: {
            coNm: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/co/ptli0905004q/${rowValues.coTin}`}>{value}</Link>;
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            grid.coList.setPage(page);
            fetch.getCoList.fetch(page);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getCoExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.coListSrch.schema.coTin} />
                            <Group.Control {...form.coListSrch.schema.coNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.coListSrch.schema.usrNm} />
                            <Group.Control {...form.coListSrch.schema.empTin} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.coListSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>

                    <Group.Section>
                        <Grid {...grid.coList.grid} data={fetch.getCoList.data?.coList} render={render} />
                    </Group.Section>

                    <Group hidden>
                        <Grid {...grid.coExcelList.grid} data={fetch.getCoExcelList.data?.coExcelList} />
                    </Group>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button
                        role="new"
                        onClick={() => {
                            navigate(URLS.ptli0905002s);
                        }}
                    />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
